import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import Lottie from 'react-lottie'

import {
   LoadingMessageType,
   MessageModel
} from '@flowwai-frontend/shared-web/ai-chat-support/data-access'
import {
   GradientRingLoader,
   useCustomT
} from '@flowwai-frontend/shared/common-components'

import { theme } from '../../theme'
import { HELP_ANY_TIME_IMG_URL } from '../../constants/ChatBoxConstants'
import DotLoader from './components/DotsLoader'
import dataLoading from './DataLoading.json'

import * as Styles from './styles'
import './styles.css'

interface Props {
   messageModel: MessageModel
   shouldAnimateText?: boolean
   loadingMessageStatus?: LoadingMessageType
   isLoading?: boolean
}

const AIChatReply = (props: Props): React.ReactElement => {
   const {
      messageModel: { content },
      isLoading,
      shouldAnimateText,
      loadingMessageStatus
   } = props

   const t = useCustomT('aiChatSupport:chatBox.dataLoadingStatus')

   const themes = theme.userQueriedChatBox

   const [displayedText, setDisplayedText] = useState('')

   useEffect(() => {
      if (!content) return

      // if (isLoading) {
      //    const interval = setInterval(() => {
      //       setDisplayedText(prev => {
      //          const updatedText = content.slice(0, prev.length + 3)

      //          if (updatedText.length >= content.length) {
      //             clearInterval(interval)
      //             return content
      //          }
      //          return updatedText
      //       })
      //    }, 30)

      //    return () => clearInterval(interval)
      // }
      // return

      setDisplayedText(content)
   }, [content])

   const renderAIChatReply = () => (
      <div className={cn(Styles.queryContainerStyles, themes.cardBorderColor)}>
         <Markdown
            components={{
               a: ({ href, children, ...props }) => (
                  <a
                     href={href}
                     target='_blank'
                     rel='noopener noreferrer'
                     {...props}
                  >
                     {children}
                  </a>
               ),
               table: ({ children, ...props }) => (
                  <div className={Styles.scrollableContainerStyles}>
                     <table {...props}>{children}</table>
                  </div>
               ),
               code: ({ children, ...props }) => (
                  <div className={Styles.scrollableContainerStyles}>
                     <code {...props}>{children}</code>
                  </div>
               )
            }}
            remarkPlugins={[remarkGfm]}
            className={cn(
               'chat-markdown-content',
               themes.messageContentTextColor
            )}
         >
            {displayedText}
         </Markdown>
      </div>
   )

   const getLoadingStatusDisplayText = (): string => {
      switch (loadingMessageStatus) {
         case 'QUERIED_TO_AI':
            return t('queriedToAI')
         case 'RETRIEVING_INFO':
            return t('retrievingInfo')
         case 'EXTRACTING_INFO':
            return t('extractingInfo')
         default:
            return ''
      }
   }

   const renderLoader = () => (
      <div
         className={cn(Styles.loaderContainer, {
            'items-center': loadingMessageStatus,
            'items-end': !loadingMessageStatus
         })}
      >
         <div className='relative'>
            <div className='min-w-[32px] min-h-[36px]'>
               <img
                  height={32}
                  width={32}
                  src={HELP_ANY_TIME_IMG_URL}
                  alt='Help Anytime'
                  className='p-[2px] mt-1 ml-1'
               />
            </div>
            <GradientRingLoader
               className='absolute top-0'
               height={40}
               width={40}
            />
         </div>
         {loadingMessageStatus ? (
            <div className='flex items-center border border-gray-300 rounded-[12px] pl-3'>
               <p className='text-sm-medium text-gray-700'>
                  {getLoadingStatusDisplayText()}
               </p>
               <div className='w-[38px] h-[38px]'>
                  <Lottie
                     options={{
                        animationData: dataLoading,
                        loop: true,
                        rendererSettings: {
                           preserveAspectRatio: 'xMidYMid slice'
                        }
                     }}
                  />
               </div>
            </div>
         ) : (
            <DotLoader />
         )}
      </div>
   )

   return (
      <div className={Styles.containerStyles}>
         {isLoading ? (
            renderLoader()
         ) : (
            <div className={Styles.containerStyles}>
               <div>
                  <img
                     height={32}
                     width={32}
                     src={HELP_ANY_TIME_IMG_URL}
                     alt='Help Anytime'
                  />
               </div>
               {renderAIChatReply()}
            </div>
         )}
      </div>
   )
}

export default observer(AIChatReply)
